"use client";
import DateRangePicker from "@/app/components/DateRangePicker";
import { getImageUrl } from "@/lib/getImageUrl";
import { getMetricById } from "@/lib/metrics";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import { Box, Button, IconButton, Menu, MenuItem, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import dayjs, { Dayjs } from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { useTranslations } from "next-intl";
import React, { useEffect, useRef, useState } from "react";
import QuickDateRangePicker from "./DateRangeQuickPicker";
import EChartsBase from "./EChartsBase";
import { LoadingSmall } from "./Loading";
import Link from "next/link";
import { lineChartOption } from "@/lib/echartOption";
import LockIcon from "./icons/lockIcon";

dayjs.extend(isBetween);

interface ChartProps {
  metricId: number;
  start?: Dayjs | undefined;
  end?: Dayjs | undefined;
  chartHeight?: string;
  onDataChange?: (data: any, locked: boolean) => void; // 可选的回调函数，用于数据变化时通知父组件
}

export default function Chart({ metricId, start, end, chartHeight = "18.5rem", onDataChange }: ChartProps) {
  const theme = useTheme();
  const [locked, setLocked] = useState<boolean | undefined>(undefined); // 锁定状态
  const [data, setData] = useState<any>(null); // 原始数据，只保存loadData结果
  const [displayData, setDisplayData] = useState<any>(null); // 展示用采样数据
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [startDate, setStartDate] = useState<Dayjs | undefined>(start);
  const [endDate, setEndDate] = useState<Dayjs | undefined>(end);
  const t = useTranslations("Metrics");

  const metric = getMetricById(metricId);
  if (!metric) {
    throw new Error("unknown metric " + metricId);
  }

  useEffect(() => {
    loadData();
  }, [theme.palette.mode, startDate, endDate]);

  // 监听浏览器宽度变化，重新采样
  useEffect(() => {
    function handleResize() {
      if (data) {
        sampleDataAndSet(data);
      }
    }
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [data]);

  const getDataPointsCount = (width: number) => {
    if (width < 360) return 80; // 手机屏幕
    if (width < 390) return 100; // 手机屏幕
    if (width < 480) return 200; // 手机屏幕
    if (width < 768) return 400; // 大手机/小平板
    if (width < 992) return 600; // 平板
    if (width < 1280) return 800; // 小桌面
    return 1000; // 大桌面
  };

  async function loadData() {
    if (metric) {
      const start = startDate ?? dayjs("2020-01-01");
      const end = endDate ?? dayjs();
      const result = await metric.loadData(start, end);
      if (onDataChange) {
        onDataChange(result.data, !!result.locked);
      }
      if (result.locked) {
        setLocked(true);
      } else {
        setData(result.data); // 保存原始数据
        sampleDataAndSet(result.data); // 采样并设置展示数据
      }
    }
  }

  // 采样并更新 displayData
  const sampleDataAndSet = (rawData: any) => {
    if (!rawData || !metric) return;
    const dataCopy = JSON.parse(JSON.stringify(rawData));
    const width = typeof window !== "undefined" ? window.innerWidth : 1280;
    const targetPoints = getDataPointsCount(width);
    try {
      // debug: 打印采样前数据长度
      if (Array.isArray(dataCopy) && dataCopy.length > 1 && Array.isArray(dataCopy[0])) {
        const xData = dataCopy[0];
        if (
          xData.length > targetPoints &&
          dataCopy.slice(1).every((arr: any) => Array.isArray(arr) && arr.length === xData.length)
        ) {
          const keepIndices = new Set<number>();
          const dataLength = xData.length;
          const interval = (dataLength - 1) / (targetPoints - 1);
          for (let i = 0; i < targetPoints; i++) {
            const position = Math.round(dataLength - 1 - i * interval);
            keepIndices.add(position);
          }
          const sortedIndices = Array.from<number>(keepIndices).sort((a, b) => a - b);
          dataCopy[0] = sortedIndices.map((idx) => xData[idx]);
          for (let i = 1; i < dataCopy.length; i++) {
            dataCopy[i] = sortedIndices.map((idx) => dataCopy[i][idx]);
          }
        } else {
          // console.log("无需采样，数据点数:", xData.length);
        }
      }
    } catch (error) {
      console.error("err:", error);
    }
    setDisplayData(dataCopy);
  };

  // 根据 displayData 生成 option
  const getOptionFromData = (sampledData: any) => {
    if (!sampledData || !metric) return null;
    const option = metric.chartOption(sampledData, t);
    option.graphic = {
      id: "watermark",
      type: "image",
      right: option.grid?.right || 100,
      bottom: 70,
      style: {
        image: getImageUrl("watermark.png", theme.palette.mode),
        width: 148,
        height: 37,
      },
    };
    return option;
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleDateRangeChange = (start: Dayjs | undefined, end: Dayjs | undefined) => {
    setStartDate(start);
    setEndDate(end);
  };

  // 每次渲染时根据 displayData 生成 option
  let option = displayData ? getOptionFromData(displayData) : null;
  if (locked) {
    option = getFakeOption(); // 如果锁定，使用假数据
  }

  return (
    <Box
      sx={{
        py: 2,
        pl: { xs: 1, sm: 2 },
        backgroundColor: "background.paperMedium",
        borderRadius: 3,
        width: "100%",
        position: "relative",
      }}>
      <div>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            pr: { xs: 2, sm: 2 },
            pl: { xs: 1, sm: 2 },
            pt: 1,
            pb: 2,
            gap: 2,
          }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip
              title={t(`${metric.i18nKey}.title` as any)}
              placement="bottom"
              slotProps={{
                tooltip: {
                  sx: (theme: any) => ({
                    backgroundColor: "background.paperExtreme",
                    color: theme.palette.text.secondary,
                    fontSize: "0.75rem",
                    p: "0.75rem",
                    borderRadius: "0.5rem",
                  }),
                },
                popper: {
                  modifiers: [
                    {
                      name: "offset",
                      options: {
                        offset: [0, -12],
                      },
                    },
                  ],
                },
              }}>
              <Typography
                variant="h5"
                sx={{
                  fontSize: "var(--font-size-s)",
                  fontWeight: 600,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}>
                {t(`${metric.i18nKey}.title` as any)}
              </Typography>
            </Tooltip>
          </Box>
          <Box sx={{ display: "flex", alignItems: "center", color: "text.secondary", gap: 3 }}>
            <QuickDateRangePicker onChange={handleDateRangeChange} />
            <IconButton sx={{ p: 0 }} onClick={handleMenuOpen}>
              <CalendarTodayOutlinedIcon sx={{ fontSize: "1.25rem", color: "text.secondary" }} />
            </IconButton>
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "center",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "center",
              }}>
              <MenuItem>
                <DateRangePicker onDateRangeChange={handleDateRangeChange} />
              </MenuItem>
            </Menu>
          </Box>
        </Box>
        {option ? (
          <EChartsBase option={option} theme={theme.palette.mode} style={{ height: chartHeight, width: "100%" }} />
        ) : (
          <LoadingSmall sx={{ height: chartHeight }} />
        )}
      </div>
      {/* Locked Mask */}
      {locked && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backdropFilter: "blur(10px)",
            borderRadius: 3,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            zIndex: 10,
            pointerEvents: "all",
          }}>
          <Link href="/membership">
            <Button
              variant="contained"
              size="large"
              sx={{
                minWidth: "14rem",
                fontWeight: 600,
                display: "flex",
                alignItems: "center",
              }}>
              <Box component="span" sx={{ mr: 1 }}>
                <LockIcon width="1.5rem" height="1.5rem" color="#F2F2F2" />
              </Box>
              {t("unlockChart")}
            </Button>
          </Link>
        </Box>
      )}
    </Box>
  );
}

const getFakeOption = () => {
  const dates = [
    "2025-1-1",
    "2025-1-2",
    "2025-1-3",
    "2025-1-4",
    "2025-1-5",
    "2025-1-6",
    "2025-1-7",
    "2025-1-8",
    "2025-1-9",
    "2025-1-10",
    "2025-1-11",
    "2025-1-12",
    "2025-1-13",
    "2025-1-14",
    "2025-1-15",
    "2025-1-16",
    "2025-1-17",
    "2025-1-18",
    "2025-1-19",
    "2025-1-20",
  ];
  const group1 = [
    12.34, 56.78, 89.01, 23.45, 67.89, 45.67, 78.9, 34.56, 90.12, 43.21, 76.54, 32.1, 98.76, 54.32, 87.65, 21.09, 65.43,
    10.98, 78.65, 43.21,
  ];
  const group2 = [
    52.48, 43.21, 61.75, 38.92, 55.67, 47.83, 59.12, 42.35, 51.98, 46.72, 58.34, 41.09, 63.87, 39.45, 56.23, 44.78,
    60.91, 37.64, 54.32, 48.96,
  ];

  const option = lineChartOption({ title: "", data: dates }, [
    { title: "", data: group1 },
    { title: "", data: group2 },
  ]);
  return option;
};
